<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted } from 'vue';
import { useAuthStore, useMessageCenterStore } from '@/store';
import Message from './components/Message.vue';
import InputMessage from './components/InputMessage.vue';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';

const messageCenter = useMessageCenterStore();
const { sortedMessages, loading } = storeToRefs(messageCenter);
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const { error } = storeToRefs(messageCenter);

const hasError = computed(() => !!error.value);

onMounted(() => init());

const init = () => {
  listenScroll();
};

const onLoadMoreMessages = async () => {
  messageCenter.getMoreMessages();
};

const listenScroll = () => {
  const scrollElement = document.getElementById('chatScrollElement');

  if (!scrollElement) {
    return setTimeout(listenScroll, 1000);
  }

  scrollElement.addEventListener('scroll', async () => {
    if (-scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight) {
      await onLoadMoreMessages();
    }
  });
};

const removeError = () => {
  messageCenter.error = undefined;
};

const onSentNewMessage = async (message: FormData) => {
  await messageCenter.sendMessage(message);

  if (hasError.value) {
    const message = error.value.data.errors
      ? Object.values(error.value.data.errors).flat().join(', ')
      : error.value.data.message;

    messageCenter.doToast(message, 'Error', true, 5000);
    removeError();
  }
};

const isYou = (sender: { id: number }) => {
  return sender.id === user?.value?.id;
};
</script>

<template>
  <div class="bg-my-gray border-round-md min-h-full flex flex-column justify-content-between p-1 fadeinright">
    <section
      v-if="can([PERMISSIONS.MESSAGE_INDEX])"
      id="chatScrollElement"
      class="flex flex-column-reverse section-height p-1 overflow-y-scroll"
    >
      <Message
        v-for="(message, index) in sortedMessages"
        :key="index"
        :created-at="message.created_at"
        :message-id="message.id"
        :content="message.content"
        :attachments="message.attachments"
        :display-name="message.sender.display_name"
        :is-you="isYou(message.sender)"
      />
      <span v-if="loading" class="text-center fadein animation-duration-1000 animation-iteration-infinite">...</span>
    </section>
    <InputMessage v-if="can([PERMISSIONS.MESSAGE_STORE])" @message-sent="onSentNewMessage" />
  </div>
</template>
